import React, { createContext, useCallback, useRef, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { WithChildren } from '../models/children-props';

interface ScrollContextType {
  setOnScrollTop: (handler: () => void) => void;
  setOnScrollBottom: (handler: () => void) => void;
  getScrollPosition: () => number;
}

export const ScrollContext = createContext<ScrollContextType>({
  setOnScrollTop: () => {
    // ...
  },
  setOnScrollBottom: () => {
    // ...
  },
  getScrollPosition: () => 0
});

type ScrollProviderProps = WithChildren & {
  scrollToTopHandler?: () => void;
  scrollToBottomHandler?: () => void;
  className?: string;
};

export const ScrollProvider = ({
  children,
  className,
  scrollToBottomHandler,
  scrollToTopHandler
}: ScrollProviderProps): JSX.Element => {
  const scrollableDivRef = useRef<HTMLDivElement>(null);
  const scrollTopHandlerRef = useRef<(() => void) | undefined>();
  const scrollBottomHandlerRef = useRef<(() => void) | undefined>();
  const scrollPosRef = useRef<number>(0);

  const setOnScrollTop = useCallback((handler: () => void) => {
    scrollTopHandlerRef.current = handler;
  }, []);

  const setOnScrollBottom = useCallback((handler: () => void) => {
    scrollBottomHandlerRef.current = handler;
  }, []);

  const getScrollPosition = useCallback(() => scrollPosRef.current, []);

  useEffect(() => {
    const scrollHandler = () => {
      const target = scrollableDivRef.current;

      if (!target) return;

      scrollPosRef.current = target.scrollTop;

      const currentScrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      const maxScrollTop = scrollHeight - clientHeight;

      if (currentScrollTop === 0) {
        scrollTopHandlerRef.current?.();
      } else if (currentScrollTop + 5 >= maxScrollTop) {
        scrollBottomHandlerRef.current?.();
      }
    };

    const debouncedHandleScroll = debounce(scrollHandler, 100);

    const currentScrollableDiv = scrollableDivRef.current;

    if (currentScrollableDiv) {
      currentScrollableDiv.addEventListener('scroll', debouncedHandleScroll);
    }

    return () => {
      if (currentScrollableDiv) {
        currentScrollableDiv.removeEventListener('scroll', debouncedHandleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (scrollToTopHandler) {
      scrollTopHandlerRef.current = scrollToTopHandler;
    }
    if (scrollToBottomHandler) {
      scrollBottomHandlerRef.current = scrollToBottomHandler;
    }
  }, [scrollToBottomHandler, scrollToTopHandler]);

  return (
    <ScrollContext.Provider value={{ setOnScrollTop, setOnScrollBottom, getScrollPosition }}>
      <div ref={scrollableDivRef} style={{ overflowY: 'auto' }} className={className}>
        {children}
      </div>
    </ScrollContext.Provider>
  );
};
